import {
  fetchPrimaryCareData,
  PrimaryCareApiResponse,
  searchPrimaryCareData,
} from "../../../api/bigQuery/pharmaPrimaryCareOrders";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { OrganizationType } from "../../../enums/organizationTypes";
import { logError } from "../../../utils/customLogger";
import { PrimaryCareFilterPayload } from "../hooks/useFilterPayload";
import { PrimaryCareTableState } from "../hooks/usePrimaryCareTableData";

/**
 * makePostCallForInitialDataFetch - API Call for Initial Load
 *
 * Purpose: Fetches initial data when component mounts.
 * This is called once on component mount to load the first page of data.
 *
 * @param signal - AbortSignal for cancelling the request
 * @param payload - Filter payload containing search params
 * @param setPrimaryCareState - State setter for API response
 * @param setTableData - State setter for table data
 */
export const makePostCallForInitialDataFetch = async (
  signal: AbortSignal,
  payload: PrimaryCareFilterPayload,
  setPrimaryCareState: React.Dispatch<
    React.SetStateAction<PrimaryCareTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<AestraPrimaryCareTable[]>>,
): Promise<void> => {
  try {
    setPrimaryCareState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    // const selectedOrganization =
    //   getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";
    const selectedOrganization = OrganizationType.ASTERA_PRIMARY_CARE;

    // Use search endpoint if keyword exists, otherwise use regular fetch
    const response: PrimaryCareApiResponse = payload.keyword
      ? await searchPrimaryCareData(
          {
            ...payload,
            org_id: selectedOrganization,
          },
          signal,
        )
      : await fetchPrimaryCareData(
          {
            ...payload,
            org_id: selectedOrganization,
          },
          signal,
        );

    if (response.details?.rows) {
      setPrimaryCareState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });
      setTableData(response.details.rows);
    } else {
      setPrimaryCareState((prev) => ({
        ...prev,
        loading: false,
        error: response.message || "Failed to fetch data",
      }));
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setPrimaryCareState((prev) => ({
      ...prev,
      loading: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForInitialDataFetch");
  }
};
